import { useMemo } from 'react';
import { useSetAtom } from 'jotai';
import { X, FileImage, FileVideo } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { selectedFileAtom } from '@/hooks/atoms';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface SelectedMediaCardProps {
  file: File;
}

export default function SelectedMediaCard({ file }: SelectedMediaCardProps) {
  const setSelectedFile = useSetAtom(selectedFileAtom);
  const isImage = file.type.startsWith('image/');
  const previewUrl = useMemo(() => {
    if (isImage) return URL.createObjectURL(file);
    return null;
  }, [file, isImage]);

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Preview */}
          <div className="flex-shrink-0 w-32 h-24 rounded-md overflow-hidden bg-muted flex items-center justify-center">
            {previewUrl ? (
              <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
            ) : (
              <FileVideo className="h-8 w-8 text-muted-foreground" />
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-sm truncate">{file.name}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    {isImage ? <FileImage className="h-3 w-3" /> : <FileVideo className="h-3 w-3" />}
                    {file.type.split('/')[1]?.toUpperCase()}
                  </span>
                  <span>{formatBytes(file.size)}</span>
                </div>
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedFile(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
